/**
 * Uses awardsStore for artist awards (api/v2/artist/awards/).
 * Returns list with displayText = "name - description - year".
 * When no data, returns placeholders like "Award or achievement #1".
 */

import { useEffect } from "react";
import { useAwardsStore } from "@/stores/awardsStore";
import type { ArtistAwardApiItem } from "@/services/artistService";

const PLACEHOLDER_COUNT = 6;

export interface DisplayAwardItem {
  id: number | string;
  name: string;
  description: string | null;
  year: number | null;
  displayText: string;
  isPlaceholder: boolean;
}

function formatAwardItem(item: ArtistAwardApiItem): DisplayAwardItem {
  const name = item.name?.trim() || "";
  const description =
    item.description != null && String(item.description).trim() !== ""
      ? String(item.description).trim()
      : null;
  const year =
    item.date != null && item.date.trim() !== ""
      ? new Date(item.date).getFullYear()
      : null;
  const parts = [name, description, year != null ? String(year) : null].filter(
    Boolean
  ) as string[];
  const displayText = parts.length > 0 ? parts.join(" - ") : "Award";

  return {
    id: item.id ?? 0,
    name,
    description,
    year,
    displayText,
    isPlaceholder: false,
  };
}

export function placeholderItem(index: number): DisplayAwardItem {
  const n = index + 1;
  return {
    id: `placeholder-${n}`,
    name: `Add Award / Achievement #${n}`,
    description: null,
    year: null,
    displayText: `Add Award / Achievement #${n}`,
    isPlaceholder: true,
  };
}

export function useArtistAwards() {
  const data = useAwardsStore((s) => s.data);
  const ensureFetched = useAwardsStore((s) => s.ensureFetched);
  const isLoading = useAwardsStore((s) => s.isLoading);
  const fetchAwards = useAwardsStore((s) => s.fetchAwards);

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  const raw = data;
  const hasData = Array.isArray(raw) && raw.length > 0;
  const awards: DisplayAwardItem[] = hasData
    ? raw.map(formatAwardItem)
    : Array.from({ length: PLACEHOLDER_COUNT }, (_, i) => placeholderItem(i));

  return {
    awards,
    hasAwards: hasData,
    isLoading,
    isError: false,
    refetch: fetchAwards,
  };
}
